/**
 * Request Quote Modal Component
 */

export default class QuoteModal {
    constructor() {
        this.modal = document.getElementById('quote-modal');
        this.formContainer = document.getElementById('quote-form-container');
        this.successState = document.getElementById('quote-success-state');
        this.categoryField = null;
        
        if (!this.modal) {
            console.error('QuoteModal: Modal element #quote-modal not found in DOM');
            return;
        }
        
        this.init();
    }
    
    init() {
        // Bind event listeners
        this.bindTriggers();
        this.bindCloseButtons();
        this.bindOverlayClick();
        this.bindEscapeKey();
        this.bindFormSubmit();
        this.bindSubmitAnother();
        
        // Auto-populate category if available
        this.autoPopulateCategory();
    }
    
    /**
     * Bind all "Request a Quote" buttons to open modal
     */
    bindTriggers() {
        // Use event delegation to handle dynamically added buttons
        document.addEventListener('click', (e) => {
            const target = e.target.closest('[href="#contact"], .quote-trigger, .request-quote-btn');
            
            if (target) {
                e.preventDefault();
                this.open();
            }
        });
    }
    
    /**
     * Bind close buttons
     */
    bindCloseButtons() {
        const closeButtons = this.modal.querySelectorAll('.quote-modal__close');
        
        closeButtons.forEach(button => {
            button.addEventListener('click', () => {
                this.close();
            });
        });
    }
    
    /**
     * Close modal when clicking overlay
     */
    bindOverlayClick() {
        const overlay = this.modal.querySelector('.quote-modal__overlay');
        
        if (overlay) {
            overlay.addEventListener('click', () => {
                this.close();
            });
        }
    }
    
    /**
     * Close modal on Escape key
     */
    bindEscapeKey() {
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.modal.style.display !== 'none') {
                this.close();
            }
        });
    }
    
    /**
     * Handle form submission
     */
    bindFormSubmit() {
        // Listen for Contact Form 7 success event
        document.addEventListener('wpcf7mailsent', (event) => {
            // Show success state
            this.showSuccess();
        }, false);
        
        // Listen for Contact Form 7 error event
        document.addEventListener('wpcf7mailfailed', (event) => {
            console.error('Form submission failed');
        }, false);
    }
    
    /**
     * Bind "Submit Another Request" button
     */
    bindSubmitAnother() {
        const submitAnotherBtn = document.getElementById('quote-submit-another');
        
        if (submitAnotherBtn) {
            submitAnotherBtn.addEventListener('click', () => {
                this.resetForm();
            });
        }
    }
    
    /**
     * Auto-populate category field based on current page
     */
    autoPopulateCategory() {
        if (!window.quoteModalData) return;
        
        const { currentCategory, hideCategory } = window.quoteModalData;
        
        // Wait for form to be loaded
        setTimeout(() => {
            this.categoryField = document.getElementById('product-category-field');
            
            if (this.categoryField && currentCategory) {
                // Find and select the matching option
                const options = this.categoryField.querySelectorAll('option');
                options.forEach(option => {
                    if (option.value === currentCategory || option.textContent.trim() === currentCategory) {
                        option.selected = true;
                    }
                });
            }
            
            // Hide category field if on homepage (optional)
            if (hideCategory && this.categoryField) {
                const categoryWrapper = this.categoryField.closest('.wpcf7-form-control-wrap');
                if (categoryWrapper && categoryWrapper.parentElement) {
                    categoryWrapper.parentElement.style.display = 'none';
                }
            }
        }, 100);
    }
    
    /**
     * Open modal
     */
    open() {
        this.modal.style.display = 'block';
        document.body.style.overflow = 'hidden';
        
        // Trigger animation
        setTimeout(() => {
            this.modal.classList.add('is-open');
        }, 10);
        
        // Focus first input
        setTimeout(() => {
            const firstInput = this.modal.querySelector('input[type="text"], input[type="email"]');
            if (firstInput) {
                firstInput.focus();
            }
        }, 300);
    }
    
    /**
     * Close modal
     */
    close() {
        this.modal.classList.remove('is-open');
        
        setTimeout(() => {
            this.modal.style.display = 'none';
            document.body.style.overflow = '';
            
            // Reset to form view if showing success
            if (this.successState.style.display !== 'none') {
                this.resetForm();
            }
        }, 300);
    }
    
    /**
     * Show success state
     */
    showSuccess() {
        this.formContainer.style.display = 'none';
        this.successState.style.display = 'block';
        
        // Scroll to top of modal
        const modalContent = this.modal.querySelector('.quote-modal__content');
        if (modalContent) {
            modalContent.scrollTop = 0;
        }
    }
    
    /**
     * Reset form and show form view
     */
    resetForm() {
        this.successState.style.display = 'none';
        this.formContainer.style.display = 'block';
        
        // Reset Contact Form 7 form
        const form = this.modal.querySelector('.wpcf7-form');
        if (form) {
            form.reset();
            
            // Clear CF7 response messages
            const responseOutput = form.querySelector('.wpcf7-response-output');
            if (responseOutput) {
                responseOutput.style.display = 'none';
                responseOutput.innerHTML = '';
            }
            
            // Remove validation classes
            const invalidFields = form.querySelectorAll('.wpcf7-not-valid');
            invalidFields.forEach(field => {
                field.classList.remove('wpcf7-not-valid');
            });
            
            const validationErrors = form.querySelectorAll('.wpcf7-not-valid-tip');
            validationErrors.forEach(error => {
                error.remove();
            });
        }
        
        // Re-populate category if needed
        this.autoPopulateCategory();
        
        // Scroll to top
        const modalContent = this.modal.querySelector('.quote-modal__content');
        if (modalContent) {
            modalContent.scrollTop = 0;
        }
    }
}
